import { Col, DatePicker, Form, Input, Modal, Row, Select } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useTaskTypes } from "@/modules/task-types/hooks/useTaskTypes";
import { TASK_PRIORITY_OPTIONS } from "../constants";
import { useUpdateTask } from "../hooks/useTasks";
import { useTasksStore } from "../store/tasks.store";
import type { UpdateTaskRequest } from "../types";

interface EditFormValues {
	title?: string;
	typeId?: string;
	priority?: UpdateTaskRequest["priority"];
	dueDate?: dayjs.Dayjs | null;
	description?: string;
}

export function TaskEditModal() {
	const [form] = Form.useForm<EditFormValues>();
	const { editModalOpen, selectedTask, closeEditModal } = useTasksStore();

	const { mutate: update, isPending } = useUpdateTask();

	const { data: taskTypesData } = useTaskTypes({ page: 1, limit: 100 });
	const taskTypes = taskTypesData?.data?.items ?? [];

	useEffect(() => {
		if (editModalOpen && selectedTask) {
			form.setFieldsValue({
				title: selectedTask.title,
				typeId: selectedTask.typeId,
				priority: selectedTask.priority,
				dueDate: selectedTask.dueDate ? dayjs(selectedTask.dueDate) : null,
				description: selectedTask.description,
			});
		}
	}, [editModalOpen, selectedTask, form]);

	const handleClose = () => {
		closeEditModal();
		form.resetFields();
	};

	const handleSubmit = () => {
		form
			.validateFields()
			.then((values) => {
				if (!selectedTask) {
					return;
				}
				const data: UpdateTaskRequest = {
					title: values.title,
					typeId: values.typeId,
					priority: values.priority,
					dueDate: values.dueDate ? values.dueDate.toISOString() : null,
					description: values.description,
				};
				update({ id: selectedTask.id, data }, { onSuccess: handleClose });
			})
			.catch(() => {
				// Validation failed - Form shows errors inline
			});
	};

	return (
		<Modal
			title={`Topshiriqni tahrirlash: ${selectedTask?.title ?? ""}`}
			open={editModalOpen}
			onCancel={handleClose}
			onOk={handleSubmit}
			confirmLoading={isPending}
			okText="Saqlash"
			cancelText="Bekor qilish"
			destroyOnHidden
			width={720}
		>
			<Form form={form} layout="vertical" requiredMark={false} className="mt-4">
				<Row gutter={16}>
					<Col span={24}>
						<Form.Item
							name="title"
							label="Sarlavha"
							rules={[
								{ required: true, message: "Sarlavhani kiriting" },
								{ max: 255, message: "Maksimum 255 ta belgi" },
							]}
						>
							<Input placeholder="Masalan: Ko'chani supirish" />
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={16}>
					<Col span={12}>
						<Form.Item
							name="typeId"
							label="Topshiriq turi"
							rules={[{ required: true, message: "Topshiriq turini tanlang" }]}
						>
							<Select
								placeholder="Topshiriq turini tanlang"
								showSearch
								optionFilterProp="label"
								options={taskTypes.map((t) => ({ value: t.id, label: t.name }))}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item name="priority" label="Ustuvorlik">
							<Select options={TASK_PRIORITY_OPTIONS} placeholder="Tanlang" />
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={16}>
					<Col span={12}>
						<Form.Item name="dueDate" label="Muddat">
							<DatePicker className="w-full" format="DD.MM.YYYY" />
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={16}>
					<Col span={24}>
						<Form.Item
							name="description"
							label="Izoh"
							rules={[
								{ required: true, message: "Izohni kiriting" },
								{ max: 2000, message: "Maksimum 2000 ta belgi" },
							]}
						>
							<Input.TextArea rows={3} placeholder="Qisqacha izoh" />
						</Form.Item>
					</Col>
				</Row>
			</Form>
		</Modal>
	);
}
